// Nomad-mon Go! — Feature 1: narrow spare-for-spare trading presentation.
// The server returns the eligible people/cards, labels, expiry, caps, and
// ceremony facts. This module never carries rarity bands, cooldowns, account
// thresholds, reservation math, or daily limits.

import * as audio from './audio.js';
import * as haptics from './haptics.js';
import * as identity from './identity.js';
import { artSrc } from './types.js';

const V = 'v350';
let ctx = null;
let composerCard = null;
let composerOptions = null;
let ceremonyActive = false;
const ceremonyQueue = [];
let ceremonyTimer = 0;
let ceremonyHapticTimer = 0;
let ceremonySound = null;
let activeCeremonyId = null;

const $ = (id) => document.getElementById(id);

function mutationKey() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return [...bytes].map(n => n.toString(16).padStart(2, '0')).join('');
}

function cardData(id) { return ctx.byId[id] || { id, name: id, rarity: 'common' }; }
function art(id) { return artSrc(cardData(id), V); }
// polB: the server's labels arrive in caps ('OFFER DECLINED', 'MYTHIC · ALT'); panels read them in sentence case
function sentence(label) {
  const t = String(label || '').toLowerCase();
  return t ? t[0].toUpperCase() + t.slice(1) : '';
}
const make = (tag, cls, text) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  if (tag === 'button') e.type = 'button';
  return e;
};
// polB: every step change inside a panel slides in (transform/opacity only); dir -1 = going back
function stepIn(body, dir = 1) {
  body.classList.remove('trade-step-in', 'trade-step-back');
  void body.offsetWidth;
  body.classList.add(dir < 0 ? 'trade-step-back' : 'trade-step-in');
}
// polB: a designed empty / error state: one glyph, one line, one note, ONE action
function stateNode({ glyph = 'swap', title, note, action, onAction, tone = '' }) {
  const box = make('div', `trade-empty${tone ? ` ${tone}` : ''}`);
  const g = make('div', 'trade-empty-glyph');
  g.innerHTML = GLYPHS[glyph] || GLYPHS.swap;
  box.append(g, make('h3', null, title));
  if (note) box.append(make('p', null, note));
  if (action) {
    const b = make('button', 'trade-primary', action);
    b.addEventListener('click', onAction);
    box.append(b);
  }
  return box;
}
const GLYPHS = {
  swap: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7h11l-3-3M17 17H6l3 3"/></svg>',
  friends: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 19c.6-3.2 2.7-5 5.5-5s4.9 1.8 5.5 5"/><circle cx="17" cy="9" r="2.5"/><path d="M16 14.2c2.4.2 4 1.8 4.5 4.3"/></svg>',
  offline: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9.5a14 14 0 0 1 18 0M6.5 13a9 9 0 0 1 11 0M10 16.5a4 4 0 0 1 4 0"/><path d="M4 4l16 16"/></svg>',
  lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10.5" width="14" height="9.5" rx="2.5"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/></svg>',
};

function miniCard(id, caption, asButton = false) {
  const d = cardData(id);
  const el = document.createElement(asButton ? 'button' : 'div');
  if (asButton) el.type = 'button';
  el.className = 'trade-mini';
  const img = document.createElement('img');
  img.alt = '';
  img.src = art(id);
  img.style.objectPosition = ctx.thumbPos(d);
  const text = document.createElement('span');
  text.className = 'trade-mini-name';
  text.textContent = d.name;
  const cap = document.createElement('small');
  cap.textContent = caption || '';
  el.dataset.rarity = d.rarity || 'common';
  const frame = document.createElement('span');
  frame.className = 'trade-mini-art';
  frame.append(img);
  el.append(frame, text, cap);
  return el;
}

function pairNode(gaveId, receivedId, leftLabel = 'You give', rightLabel = 'You get') {
  const pair = document.createElement('div');
  pair.className = 'trade-pair';
  const arrow = make('b', 'trade-arrow');
  arrow.innerHTML = GLYPHS.swap;
  pair.append(miniCard(gaveId, leftLabel), arrow, miniCard(receivedId, rightLabel));
  return pair;
}

export async function quote(card) {
  if (!identity.canTradeSpares()) return { visible: false, canTrade: false, friends: [] };
  return identity.spareTradeOptions(card.id);
}

export function openComposer(card, options) {
  if (!card || !options?.canTrade) return;
  audio.loadAll();
  composerCard = card;
  composerOptions = options;
  const panel = $('tradeComposePanel');
  panel.classList.remove('confirm', 'sent');
  $('tradeComposeTitle').textContent = 'Trade with';
  $('tradeComposeBody').textContent = '';
  $('tradeComposeStatus').textContent = '';
  renderFriends();
  $('tradeCompose').classList.add('open');
  $('tradeCompose').setAttribute('aria-hidden', 'false');
  audio.play('whoosh', { gain: 0.32, rate: 1.12 });
}

function closeComposer() {
  $('tradeCompose').classList.remove('open');
  $('tradeCompose').setAttribute('aria-hidden', 'true');
  composerCard = null;
  composerOptions = null;
}

function renderFriends(dir = 1) {
  const body = $('tradeComposeBody');
  body.textContent = '';
  $('tradeComposePanel').classList.remove('confirm');
  const friends = composerOptions.friends || [];
  // polB: no friend has a matching spare -> say so, with one way forward (was: an empty panel)
  if (!friends.length) {
    $('tradeComposeTitle').textContent = 'Trade';
    body.appendChild(stateNode({ glyph: 'friends', title: 'No matches yet',
      note: 'Friends with a spare you are missing show up here.',
      action: 'Invite a friend', onAction: inviteFriend }));
    stepIn(body, dir);
    return;
  }
  $('tradeComposeTitle').textContent = 'Trade with';
  body.appendChild(miniCard(composerCard.id, 'Your spare'));
  body.lastChild.classList.add('trade-hero');
  const list = make('div', 'trade-friends');
  for (const friend of friends) {
    const button = make('button', 'trade-friend');
    const face = make('span', 'trade-friend-face', (friend.name || '?').trim().charAt(0).toUpperCase());
    const who = make('span', 'trade-friend-who');
    who.append(make('b', null, friend.name),
      make('small', null, `${friend.cards.length} matching ${friend.cards.length === 1 ? 'spare' : 'spares'}`));
    const fan = make('span', 'trade-friend-fan');
    for (const fact of friend.cards.slice(0, 3)) {
      const img = document.createElement('img');
      img.alt = ''; img.src = art(fact.cardId);
      img.style.objectPosition = ctx.thumbPos(cardData(fact.cardId));
      fan.append(img);
    }
    button.append(face, who, fan);
    button.addEventListener('click', () => { audio.play('tick', { gain: 0.42 }); renderFriendCards(friend); });
    list.appendChild(button);
  }
  body.appendChild(list);
  stepIn(body, dir);
}

async function inviteFriend() {
  const url = location.origin + location.pathname;
  const text = 'Trade spares with me on Nomad-mon Go!';
  try {
    if (navigator.share) { await navigator.share({ title: 'Nomad-mon Go!', text, url }); return; }
  } catch (e) { if (e?.name === 'AbortError') return; }
  open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank', 'noopener');
}

function renderFriendCards(friend, dir = 1) {
  $('tradeComposeTitle').textContent = `${friend.name}'s spares`;
  $('tradeComposePanel').classList.remove('confirm');
  const body = $('tradeComposeBody');
  body.textContent = '';
  const carousel = document.createElement('div');
  carousel.className = 'trade-card-carousel';
  for (const fact of friend.cards) {
    const card = miniCard(fact.cardId, sentence(fact.rarityLabel), true);
    card.addEventListener('click', () => { audio.play('deckpick', { gain: 0.5 }); renderConfirm(friend, fact); });
    carousel.appendChild(card);
  }
  const back = make('button', 'trade-text-btn', '‹ Friends');
  back.addEventListener('click', () => renderFriends(-1));
  body.append(carousel, back);
  stepIn(body, dir);
}

function renderConfirm(friend, requested) {
  $('tradeComposeTitle').textContent = `Trade with ${friend.name}`;
  const body = $('tradeComposeBody');
  body.textContent = '';
  body.appendChild(pairNode(composerCard.id, requested.cardId));
  const send = make('button', 'trade-primary', 'Send offer');
  send.addEventListener('click', async () => {
    if (send.disabled) return;
    send.disabled = true;
    try {
      const result = await identity.createSpareTrade(friend.name, composerCard.id,
        requested.cardId, mutationKey());
      $('tradeComposePanel').classList.add('sent');
      $('tradeComposeStatus').textContent = sentence(result.noticeLabel);
      audio.play('slam', { gain: 0.55, rate: 1.18 });
      haptics.buzz([18, 30, 22]);
      setTimeout(() => { closeComposer(); refreshBadge(); }, 760);
    } catch (err) {
      send.disabled = false;
      ctx.toast(err.message || 'That spare is no longer available.');
    }
  });
  const back = make('button', 'trade-text-btn', `‹ ${friend.name}'s spares`);
  back.addEventListener('click', () => renderFriendCards(friend, -1));
  body.append(send, back);
  $('tradeComposePanel').classList.add('confirm');
  stepIn(body, 1);
}

function renderOffer(row, incoming) {
  const item = document.createElement('article');
  item.className = `trade-offer${incoming ? ' incoming' : ''}`;
  const heading = document.createElement('h3');
  heading.textContent = incoming ? `${row.proposer} offers a swap` : `Waiting for ${row.recipient}`;
  item.append(heading, pairNode(row.offeredCardId, row.requestedCardId,
    incoming ? 'You get' : 'You give', incoming ? 'You give' : 'You get'));
  const expiry = document.createElement('div');
  expiry.className = 'trade-expiry';
  expiry.textContent = sentence(row.expiryLabel);
  const actions = document.createElement('div');
  actions.className = 'trade-actions';
  if (incoming) {
    const accept = make('button', 'trade-primary', 'Accept');
    const decline = make('button', null, 'Decline');
    accept.addEventListener('click', async () => {
      accept.disabled = true; decline.disabled = true;
      try {
        const result = await identity.acceptTrade(row.id, mutationKey());
        ctx.refreshOwnership();
        await renderInbox();
        if (result.ceremony) playCeremony(result.ceremony);
      } catch (err) { ctx.toast(err.message || 'That spare is no longer available.'); await renderInbox(); }
    });
    decline.addEventListener('click', async () => {
      accept.disabled = true; decline.disabled = true;
      try { await identity.declineTrade(row.id, mutationKey()); await renderInbox(); }
      catch (err) { ctx.toast(err.message || 'The offer already changed.'); await renderInbox(); }
    });
    actions.append(accept, decline);
  } else {
    const cancel = make('button', null, 'Cancel offer');
    cancel.addEventListener('click', async () => {
      cancel.disabled = true;
      try { await identity.cancelTrade(row.id, mutationKey()); await renderInbox(); }
      catch (err) { ctx.toast(err.message || 'The offer already changed.'); await renderInbox(); }
    });
    actions.append(cancel);
  }
  item.append(expiry, actions);
  return item;
}

// econfix: a closed offer (declined, cancelled, expired, gone stale) is shown, never silently dropped
function ago(ts) {
  const m = Math.max(0, Math.round((Date.now() - (Number(ts) || Date.now())) / 60000));
  return m < 60 ? `${Math.max(1, m)}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
}
function renderNotice(n) {
  const mine = n.proposer === identity.currentPlayer();
  const item = document.createElement('article');
  item.className = `trade-offer trade-notice${n.unread ? ' unread' : ''}`;
  const heading = document.createElement('h3');
  heading.textContent = `${sentence(n.noticeLabel || 'OFFER CLOSED')} · ${mine ? n.recipient : n.proposer}`;
  const when = document.createElement('div');
  when.className = 'trade-expiry';
  when.textContent = ago(n.resolvedTs || n.createdTs);
  item.append(heading, pairNode(n.offeredCardId, n.requestedCardId,
    mine ? 'You give' : 'You get', mine ? 'You get' : 'You give'), when);
  return item;
}
const badgeOf = (inbox) => (inbox.badgeCount || 0) + (inbox.noticeUnread || 0);
const hasAny = (inbox) => !!((inbox.incoming || []).length || (inbox.outgoing || []).length
  || (inbox.notices || []).length || badgeOf(inbox));

function sectionHead(text) { return make('div', 'trade-section', text); }

// polB: the inbox has four designed states -- loading, list, empty, and error (with Retry; a failed load
// used to leave a blank panel). `qa` (window.__nmTrade) feeds a canned inbox or a forced failure.
async function renderInbox({ dir = 1, qa = null } = {}) {
  const body = $('tradeSocialBody');
  body.textContent = '';
  if (!qa && !identity.canTradeSpares()) {
    body.appendChild(stateNode({ glyph: 'lock',
      title: identity.isGuest() ? 'Sign in to trade' : 'Trading is off here',   // qa275: was 'DEVICE CLAIM REQUIRED' (jargon)
      note: identity.isGuest() ? 'Play as a named player to swap spares with friends.' : 'This device is not set up for trades.',
      action: 'Got it', onAction: closeSocial }));
    stepIn(body, dir);
    paintBadge(0);
    return;
  }
  body.appendChild(make('div', 'trade-loading', 'Loading trades'));
  try {
    const inbox = qa ? await qa() : await identity.tradeInbox();
    body.textContent = '';
    paintBadge(badgeOf(inbox), hasAny(inbox));
    if ((inbox.incoming || []).length) body.appendChild(sectionHead('For you'));
    for (const row of inbox.incoming || []) body.appendChild(renderOffer(row, true));
    if ((inbox.outgoing || []).length) body.appendChild(sectionHead('Sent'));
    for (const row of inbox.outgoing || []) body.appendChild(renderOffer(row, false));
    if ((inbox.notices || []).length) body.appendChild(sectionHead('Recent'));
    for (const n of inbox.notices || []) body.appendChild(renderNotice(n));
    if (inbox.noticeUnread && !qa) {     // shown now: read
      identity.markTradeNoticesSeen().then(() => paintBadge(inbox.badgeCount || 0, hasAny(inbox))).catch(() => {});
    }
    if (!body.children.length) {
      body.appendChild(stateNode({ glyph: 'swap', title: 'No trades right now',
        note: 'Open a spare in your Collection to offer a swap.',
        action: ctx.openCollection ? 'Open Collection' : 'Got it',
        onAction: () => { closeSocial(); ctx.openCollection?.(); } }));
    }
    stepIn(body, dir);
    for (const ceremony of inbox.ceremonies || []) playCeremony(ceremony);
  } catch {
    body.textContent = '';
    body.appendChild(stateNode({ glyph: 'offline', tone: 'error', title: 'Couldn\u2019t load trades',
      note: 'Check your connection and try again.',
      action: 'Retry', onAction: () => { audio.play('tick', { gain: 0.42 }); renderInbox({ qa }); } }));
    stepIn(body, dir);
  }
}

// polB: the title's trade door (#tradeDoor, beside the Collection pile) is the inbox's first-class entry.
// It shows while there is anything to see; its badge is the pending count (#socialBadge, on the retired
// Friends button, is painted too).
function paintBadge(n, any = !!n) {
  const label = n > 9 ? '9+' : String(n || '');
  const badge = $('socialBadge');
  if (badge) { badge.textContent = label; badge.hidden = !n; }
  const door = $('tradeDoor');
  if (!door) return;
  door.hidden = !(any || n);
  $('tradeDoorBadge').textContent = label;
  door.classList.toggle('has-pending', !!n);
  door.setAttribute('aria-label', n ? `Trades: ${n} waiting` : 'Trades');
}

// a pending offer this device has not seen before chimes once (the ids live per player in localStorage)
function announceNew(inbox) {
  const ids = (inbox.incoming || []).map(r => String(r.id));
  const key = `nomadmon-trade-seen:${String(identity.currentPlayer() || '').toLowerCase()}`;
  let seen = [];
  try { seen = JSON.parse(localStorage.getItem(key) || '[]'); } catch { /* private */ }
  const fresh = ids.filter(id => !seen.includes(id));
  try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* private */ }
  if (fresh.length) ringDoor();
}
function ringDoor() {
  const door = $('tradeDoor');
  audio.loadSfx(['deckpick']).then(() => audio.play('deckpick', { gain: 0.7 })).catch(() => {});
  haptics.buzz([12, 40, 16]);
  if (!door) return;
  door.classList.remove('ring');
  void door.offsetWidth;
  door.classList.add('ring');
}

export async function refreshBadge() {
  if (!identity.canTradeSpares()) { paintBadge(0); return; }
  try {
    const inbox = await identity.tradeInbox();
    paintBadge(badgeOf(inbox), hasAny(inbox));
    announceNew(inbox);
    if (!$('entry').classList.contains('on') && (inbox.ceremonies || []).length) {
      await audio.loadAll();
      for (const ceremony of inbox.ceremonies) playCeremony(ceremony);
    }
  }
  catch { /* keep the last painted badge: a blip offline must not hide pending offers */ }
}

export async function openSocial(opts = {}) {
  $('tradeLive').hidden = !ctx.openLiveBattle;      // polB: the Live Battle door only while MP is on
  $('tradeSocial').classList.add('open');
  $('tradeSocial').setAttribute('aria-hidden', 'false');
  audio.play('whoosh', { gain: 0.32, rate: 1.12 });
  $('tradeSocialBody').replaceChildren(make('div', 'trade-loading', 'Loading trades'));
  await audio.loadAll();
  await renderInbox(opts);
}

function closeSocial() {
  $('tradeSocial').classList.remove('open');
  $('tradeSocial').setAttribute('aria-hidden', 'true');
}

export function playCeremony(ceremony) {
  if (!ceremony?.eventId || activeCeremonyId === ceremony.eventId
    || ceremonyQueue.some(c => c.eventId === ceremony.eventId)) return;
  ceremonyQueue.push(ceremony);
  if (!ceremonyActive) startNextCeremony();
}

function startNextCeremony() {
  const ceremony = ceremonyQueue.shift();
  if (!ceremony) { ceremonyActive = false; return; }
  ceremonyActive = true;
  activeCeremonyId = ceremony.eventId;
  const fx = $('tradeFx');
  fx._ceremony = ceremony;
  fx.dataset.eventId = ceremony.eventId;
  const gave = cardData(ceremony.gaveCardId);
  const received = cardData(ceremony.receivedCardId);
  const giveImg = fx.querySelector('.trade-fx-give img');
  const receiveImg = fx.querySelector('.trade-fx-receive img');
  giveImg.src = art(ceremony.gaveCardId); giveImg.style.objectPosition = ctx.thumbPos(gave);
  receiveImg.src = art(ceremony.receivedCardId); receiveImg.style.objectPosition = ctx.thumbPos(received);
  fx.querySelector('.trade-fx-name').textContent = received.name;
  fx.classList.remove('on');
  void fx.offsetWidth;
  requestAnimationFrame(() => {
    const timing = audio.timingMark('trade-exchange-start', { eventId: ceremony.eventId });
    fx.classList.add('on');
    ceremonySound = audio.play('tradecomplete', { gain: 0.92, timing });
    requestAnimationFrame(now => audio.timingFrame(now));
  });
  ceremonyHapticTimer = setTimeout(() => haptics.buzz([22, 25, 52]), 520);
  ceremonyTimer = setTimeout(() => finishCeremony(ceremony, false), 1560);
}

function finishCeremony(ceremony, skipped) {
  if (!ceremonyActive) return;
  clearTimeout(ceremonyTimer);
  clearTimeout(ceremonyHapticTimer);
  if (skipped && ceremonySound) { try { ceremonySound.stop(); } catch { /* ended */ } }
  ceremonySound = null;
  $('tradeFx').classList.remove('on');
  $('tradeFx')._ceremony = null;
  delete $('tradeFx').dataset.eventId;
  ceremonyActive = false;
  activeCeremonyId = null;
  identity.acknowledgeTradeCeremony(ceremony.eventId, mutationKey()).catch(() => {
    // No local latch: if acknowledgement fails, the server deliberately sends
    // the ceremony again on the next open instead of silently losing it.
  });
  identity.refreshState().then(() => ctx.refreshOwnership()).catch(() => {});
  setTimeout(startNextCeremony, 80);
}

export function initTrading(context) {
  ctx = { ...context, byId: Object.fromEntries(context.deck.map(d => [d.id, d])) };
  $('tradeComposeClose').addEventListener('click', closeComposer);
  $('tradeSocialClose').addEventListener('click', closeSocial);
  $('tradeLive').addEventListener('click', () => { closeSocial(); ctx.openLiveBattle?.(); });
  $('tradeLive').hidden = !ctx.openLiveBattle;
  $('tradeDoor')?.addEventListener('click', () => {
    audio.unlock?.();
    audio.play('tick', { gain: 0.5 });
    haptics.buzz(10);
    openSocial();
  });
  installQaHooks();
  $('tradeFx').addEventListener('pointerdown', () => {
    if (!ceremonyActive) return;
    const current = $('tradeFx').dataset.eventId;
    const ceremony = { eventId: current };
    // The real current object is retained on the element for skip-safe ack.
    finishCeremony($('tradeFx')._ceremony || ceremony, true);
  });
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshBadge();
  });
  return { openSocial, openComposer, quote, refreshBadge };
}

// polB QA: reach every trade state without real accounts or a server (the coordinator's screenshots).
//   __nmTrade.door(n)          paint the title door with n pending (0 = door with no count); ring=true chimes
//   __nmTrade.inbox(kind)      open the inbox: 'list' (canned offers), 'empty', 'error'
//   __nmTrade.compose(kind)    open the composer on the first deck card: 'friends' or 'empty'
function installQaHooks() {
  const ids = ctx.deck.slice(0, 6).map(d => d.id);
  const now = Date.now();
  const canned = () => ({
    incoming: [{ id: 'qa1', proposer: 'Ajay', recipient: 'You', offeredCardId: ids[1], requestedCardId: ids[0], expiryLabel: 'EXPIRES IN 23H' }],
    outgoing: [{ id: 'qa2', proposer: 'You', recipient: 'Sheren', offeredCardId: ids[2], requestedCardId: ids[3], expiryLabel: 'EXPIRES IN 2D' }],
    notices: [{ id: 'qa3', proposer: 'Zach', recipient: 'You', offeredCardId: ids[4], requestedCardId: ids[5], noticeLabel: 'OFFER DECLINED', resolvedTs: now - 3 * 3600e3, unread: true }],
    ceremonies: [], badgeCount: 1, noticeUnread: 1,
  });
  window.__nmTrade = {
    door: (n = 2, ring = false) => { paintBadge(n, true); if (ring) ringDoor(); return !$('tradeDoor').hidden; },
    inbox: (kind = 'list') => openSocial({ qa: async () => {
      if (kind === 'error') throw new Error('qa');
      return kind === 'empty' ? { incoming: [], outgoing: [], notices: [], ceremonies: [], badgeCount: 0 } : canned();
    } }),
    compose: (kind = 'friends') => openComposer(ctx.deck[0], { canTrade: true, friends: kind === 'empty' ? [] : [
      { name: 'Ajay', cards: ids.slice(1, 4).map(cardId => ({ cardId, rarityLabel: 'RARE' })) },
      { name: 'Sheren', cards: ids.slice(4, 5).map(cardId => ({ cardId, rarityLabel: 'MYTHIC · ALT' })) },
    ] }),
  };
}
